import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "store/hooks";
import useDebounce from "./useDebounce";

import MultiStepForm from "components/forms/MultiStepForm/MultiStepForm";
import {
  createCampaign,
  validateShortId,
  fetchCampaignByShort,
  updateCampaign,
} from "store/campaigns/campaignDetails/campaignDetails.actions";
import { resetCreateState } from "store/campaigns/campaignCreation/createCampaign.slice";
import {
  AddLocationButton,
  CreateCampaignContainer,
  DateCol,
  DateSeparator,
  DatesRow,
  DollarInputWrapper,
  DollarSymbol,
  FormStep,
  GoalInput,
  HelperText,
  ImageInput,
  InputField,
  Label,
  Required,
  Textarea
} from "./CreateCampaign.styles";
import { resetUpdateSuccess } from "store/campaigns/campaignDetails/campaignDetails.slice";

const CreateCampaign = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { loading, successId, error } = useAppSelector((s) => s.campaigns.createCampaign);

  const { shortIdValidation, updateSuccess, data: campaignDetails,  loading: updateLoading } = useAppSelector((s) => s.campaigns.campaignDetails);

  const userId = useAppSelector((state) => state.user.id);
  const { projectId, fundraiserId } = useParams();

  const [shortId, setShortId] = useState("");
  const [shortIdStatus, setShortIdStatus] = useState<
    "available" | "taken" | null
  >(null);

  const [name, setName] = useState("");
  const [defaultImage, setDefaultImage] = useState("");
  const [description, setDescription] = useState("");
  const [goal, setGoal] = useState("");
  const [startDateTime, setStartDateTime] = useState("");
  const [endDateTime, setEndDateTime] = useState("");
  const [locations, setLocations] = useState<string[]>([""]);

  const debouncedShortId = useDebounce(shortId, 1000);

  const isEditMode = Boolean(fundraiserId);
  const [originalShortId, setOriginalShortId] = useState("");

  useEffect(() => {
    if (isEditMode && fundraiserId) {
      dispatch(fetchCampaignByShort(fundraiserId));
    }
  }, [dispatch, isEditMode, fundraiserId]);

  useEffect(() => {
    if (isEditMode && campaignDetails?.short_id) {
      setShortId(campaignDetails.short_id || "");
      setOriginalShortId(campaignDetails.short_id || "");
      setName(campaignDetails.name || "");
      setDefaultImage(campaignDetails.default_image || "");
      setDescription(campaignDetails.description || "");
      setGoal(campaignDetails.goal ? String(campaignDetails.goal) : "");
      setStartDateTime(
        campaignDetails.start_date_time
          ? campaignDetails.start_date_time.replace(" ", "T").slice(0, 16)
          : ""
      );
      setEndDateTime(
        campaignDetails.end_date_time
          ? campaignDetails.end_date_time.replace(" ", "T").slice(0, 16)
          : ""
      );
      setLocations([campaignDetails.location || ""]);
    }
  }, [campaignDetails, isEditMode]);

  useEffect(() => {
    if (successId || updateSuccess) {
      toast.success(isEditMode ? "Campaign updated!" : "Campaign created!");

      if (isEditMode) {
        navigate(`/fundraising/${shortId}`);
      } else {
        navigate(-1);
      }
      dispatch(resetCreateState());
      dispatch(resetUpdateSuccess());
    }

    if (error) toast.error(error);
  }, [successId, updateSuccess, error, navigate, dispatch, isEditMode, projectId, shortId])

  useEffect(() => {
    if (!isEditMode || debouncedShortId !== originalShortId) {
      if (debouncedShortId) {
        dispatch(validateShortId(debouncedShortId));
      }
    } else {
      setShortIdStatus("available");
    }
  }, [debouncedShortId, dispatch, isEditMode, originalShortId]);
  useEffect(() => {
    if (shortIdValidation.valid === null) setShortIdStatus(null);
    else setShortIdStatus(shortIdValidation.valid ? "available" : "taken");
  }, [shortIdValidation]);

  const handleAddLocation = () => {
    setLocations([...locations, ""]);
  };

  const handleLocationChange = (index: number, value: string) => {
    const updated = [...locations];
    updated[index] = value;
    setLocations(updated);
  };

  const isFormComplete = (): boolean => {
    if (!shortId || shortIdStatus !== "available") return false;
    if (!name || !defaultImage || !description) return false;
    if (!goal || isNaN(Number(goal)) || Number(goal) <= 0) return false;
    if (!startDateTime || !endDateTime) return false;
    if (!locations[0]) return false;
    return true;
  };

const handleSubmit = async () => {
  if (!isFormComplete()) return;

  const commonPayload = {
    short_id: shortId,
    name,
    description,
    default_image: defaultImage,
    goal: Number(goal),
    location: locations[0],
    start_date_time: startDateTime.replace("T", " ") + ":00",
    end_date_time: endDateTime.replace("T", " ") + ":00",
    project_id: isEditMode
    ? campaignDetails.project_id
    : Number(projectId),
  };

  if (isEditMode && campaignDetails?.id) {
    dispatch(updateCampaign({ id: campaignDetails.id, ...commonPayload }));
  } else {
    dispatch(createCampaign({ user_id: userId, ...commonPayload }));
  }
};

  return (
    <CreateCampaignContainer>
      <MultiStepForm
        title="Create a campaign"
        onBack={() => {
          navigate(-1);
        }}
        steps={[
          {
            title: "Short ID",
            render: () => (
              <FormStep>
                <Label>
                  Campaign short ID <Required>*</Required>
                </Label>
                <InputField
                  type="text"
                  value={shortId}
                  onChange={(e) => setShortId(e.target.value)}
                  $status={
                    shortIdStatus === "taken"
                      ? "error"
                      : shortIdStatus === "available"
                      ? "success"
                      : undefined
                  }
                />
                {shortIdStatus === "taken" && (
                  <HelperText $type="error">
                    This ID is already in use.
                  </HelperText>
                )}
                {shortIdStatus === "available" && (
                  <HelperText $type="success">This ID is available.</HelperText>
                )}
              </FormStep>
            ),
            validate: () =>
              shortId.trim() !== "" &&
              (shortIdStatus === "available" || (isEditMode && shortId === originalShortId)),
          },
          {
            title: "Campaign basic info",
            render: () => (
              <FormStep>
                <Label>
                  Campaign name <Required>*</Required>
                </Label>
                <InputField
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />

                <Label>
                  Campaign image <Required>*</Required>
                </Label>
                <ImageInput
                  value={defaultImage}
                  onChange={(e) => setDefaultImage(e.target.value)}
                  placeholder="Enter image URL"
                />

                <Label>
                  Campaign description <Required>*</Required>
                </Label>
                <Textarea
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </FormStep>
            ),
            validate: () =>
              name.trim() !== "" &&
              defaultImage.trim() !== "" &&
              description.trim() !== "",
          },
          {
            title: "Donation goal",
            render: () => (
              <FormStep>
                <Label>
                  Enter the amount of money <Required>*</Required>
                </Label>
                <DollarInputWrapper>
                  <DollarSymbol>$</DollarSymbol>
                  <GoalInput
                    type="number"
                    value={goal}
                    onChange={(e) => setGoal(e.target.value)}
                  />
                </DollarInputWrapper>
              </FormStep>
            ),
            validate: () => !isNaN(Number(goal)) && Number(goal) > 0,
          },
          {
            title: "Date",
            render: () => (
              <FormStep>
                <DatesRow>
                  <DateCol>
                    <Label>
                      Campaign starts <Required>*</Required>
                    </Label>
                    <InputField
                      type="datetime-local"
                      value={startDateTime}
                      onChange={(e) => setStartDateTime(e.target.value)}
                      min={new Date().toISOString().slice(0, 16)}
                    />
                  </DateCol>
                  <DateSeparator>–</DateSeparator>
                  <DateCol>
                    <Label>
                      Campaign ends <Required>*</Required>
                    </Label>
                    <InputField
                      type="datetime-local"
                      value={endDateTime}
                      onChange={(e) => setEndDateTime(e.target.value)}
                      min={
                        startDateTime || new Date().toISOString().slice(0, 16)
                      }
                    />
                  </DateCol>
                </DatesRow>
              </FormStep>
            ),
            validate: () => startDateTime !== "" && endDateTime !== "",
          },
          {
            title: "Location(s)",
            render: () => (
              <FormStep>
                {locations.map((loc, idx) => (
                  <InputField
                    key={idx}
                    type="text"
                    value={loc}
                    onChange={(e) => handleLocationChange(idx, e.target.value)}
                    placeholder={
                      idx === 0 ? "Campaign location *" : "Additional location"
                    }
                  />
                ))}
                <AddLocationButton type="button" onClick={handleAddLocation}>
                  + Add Another Location
                </AddLocationButton>
              </FormStep>
            ),
            validate: () => locations[0].trim() !== "",
          },
        ]}
        onSubmit={handleSubmit}
        submitLabel={loading || updateLoading ? "Saving..." : isEditMode ? "Update" : "Create"}
      />
    </CreateCampaignContainer>
  );
};

export default CreateCampaign;
